import React, { useContext, useEffect, useState, useRef } from "react";
import { appContext } from "../../context/ContextProvider";
import toast from "react-hot-toast";
import { useMutation } from "@apollo/client";
import { Form, Field, Formik } from "formik";
import * as Yup from "yup";
import usersGQL from "../../gql/users";
import { useTranslation } from "react-i18next";
import "./LoginPage.css";

// Parse Permissions Helper (from Login.js)
const parsePermissions = (data) => {
  let tempData = [];
  if (Array.isArray(data)) {
    data.forEach((item) => {
      tempData.push(item.PermissionUrl || item.permission_url);
    });
  }
  return tempData;
};

/* ============================================================
   SUB-COMPONENT: DATA FLOW ANIMATION
   ============================================================ */
const DataFlowAnimation = () => {
  const stageBodyRef = useRef(null);
  const [litNodes, setLitNodes] = useState({ out: [], in: [] });
  const [packets, setPackets] = useState([]);

  // Animation constants
  const VB_W = 640;
  const VB_H = 234;
  const CYCLE = 6200;
  const GAP_OUT = 300;
  const IN_START = 2200;
  const GAP_IN = 250;

  const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

  const getPtOnPath = (pathId, t) => {
    const path = document.getElementById(pathId);
    if (!path) return null;
    const len = path.getTotalLength();
    return path.getPointAtLength(len * Math.max(0, Math.min(1, t)));
  };

  const toPx = (pt) => {
    if (!stageBodyRef.current || !pt) return { x: 0, y: 0 };
    const r = stageBodyRef.current.getBoundingClientRect();
    return {
      x: (pt.x / VB_W) * r.width,
      y: (pt.y / VB_H) * r.height,
    };
  };

  const runPacket = (id, pathId, duration, delay, type) => {
    setTimeout(() => {
      const startTime = performance.now();
      const packet = {
        id,
        x: -100,
        y: -100,
        opacity: 0,
        label:
          type === "out"
            ? ["ELG", "PRE", "ADV", "CLM", "PAY", "COM", "RPT"][id]
            : "ACK",
        type,
      };

      setPackets((prev) => [
        ...prev.filter((p) => p.id !== id || p.type !== type),
        packet,
      ]);

      const step = (now) => {
        const progress = (now - startTime) / duration;
        const e = ease(Math.min(progress, 1));
        const pt = getPtOnPath(pathId, e);

        if (pt) {
          const px = toPx(pt);
          setPackets((prev) =>
            prev.map((p) =>
              p.id === id && p.type === type
                ? { ...p, x: px.x, y: px.y, opacity: 1 }
                : p,
            ),
          );
        }

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          setPackets((prev) =>
            prev.map((p) =>
              p.id === id && p.type === type ? { ...p, opacity: 0 } : p,
            ),
          );
        }
      };
      requestAnimationFrame(step);
    }, delay);
  };

  const pulseNode = (id, type, delay, duration = 800) => {
    setTimeout(() => {
      setLitNodes((prev) => ({ ...prev, [type]: [...prev[type], id] }));
      setTimeout(() => {
        setLitNodes((prev) => ({
          ...prev,
          [type]: prev[type].filter((i) => i !== id),
        }));
      }, duration);
    }, delay);
  };

  useEffect(() => {
    let timer;
    const startCycle = () => {
      // Outbound
      for (let i = 0; i < 7; i++) {
        runPacket(i, `op${i}`, 950, i * GAP_OUT, "out");
        pulseNode(i, "out", i * GAP_OUT);
      }
      // Inbound
      for (let j = 0; j < 7; j++) {
        runPacket(j, `ip${j}`, 800, IN_START + j * GAP_IN, "in");
        pulseNode(j, "in", IN_START + j * GAP_IN + 800);
      }
      timer = setTimeout(startCycle, CYCLE);
    };

    const initialTimeout = setTimeout(startCycle, 800);
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="stage">
      <div className="stage-bar">
        <div className="s-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div className="s-title">Live NPHIES Transaction Flow</div>
        <div className="s-live">Connected</div>
      </div>

      <div className="stage-body" ref={stageBodyRef}>
        <svg
          className="route-svg"
          viewBox="0 0 640 234"
          preserveAspectRatio="none"
        >
          {/* Outbound Paths */}
          <path
            id="op0"
            d="M 155 16  C 240 16  295 117 320 117"
            fill="none"
            stroke="rgba(0,168,118,0.12)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="op1"
            d="M 155 49  C 242 49  298 117 320 117"
            fill="none"
            stroke="rgba(0,168,118,0.12)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="op2"
            d="M 155 82  C 244 82  300 117 320 117"
            fill="none"
            stroke="rgba(0,168,118,0.12)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="op3"
            d="M 155 117 C 230 117 290 117 320 117"
            fill="none"
            stroke="rgba(0,168,118,0.12)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="op4"
            d="M 155 150 C 244 150 300 117 320 117"
            fill="none"
            stroke="rgba(0,168,118,0.12)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="op5"
            d="M 155 183 C 242 183 298 117 320 117"
            fill="none"
            stroke="rgba(0,168,118,0.12)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="op6"
            d="M 155 216 C 240 216 295 117 320 117"
            fill="none"
            stroke="rgba(0,168,118,0.12)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />

          {/* Inbound Paths */}
          <path
            id="ip0"
            d="M 320 117 C 345 117 400 16  485 16 "
            fill="none"
            stroke="rgba(0,98,204,0.1)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="ip1"
            d="M 320 117 C 345 117 402 49  485 49 "
            fill="none"
            stroke="rgba(0,98,204,0.1)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="ip2"
            d="M 320 117 C 345 117 405 82  485 82 "
            fill="none"
            stroke="rgba(0,98,204,0.1)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="ip3"
            d="M 320 117 C 345 117 400 117 485 117"
            fill="none"
            stroke="rgba(0,98,204,0.1)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="ip4"
            d="M 320 117 C 345 117 405 150 485 150"
            fill="none"
            stroke="rgba(0,98,204,0.1)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="ip5"
            d="M 320 117 C 345 117 402 183 485 183"
            fill="none"
            stroke="rgba(0,98,204,0.1)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
          <path
            id="ip6"
            d="M 320 117 C 345 117 400 216 485 216"
            fill="none"
            stroke="rgba(0,98,204,0.1)"
            strokeWidth="1.4"
            strokeDasharray="5,4"
          />
        </svg>

        {/* Nodes Left */}
        <div className="col-l">
          {[
            { icon: "🪪", label: "Eligibility" },
            { icon: "📋", label: "Pre-Auth" },
            { icon: "⚡", label: "Adv PreAuth" }, // Shortened for layout
            { icon: "🏥", label: "Claims" },
            { icon: "💳", label: "Payments" },
            { icon: "💬", label: "Comm." },
            { icon: "📊", label: "Reporting" },
          ].map((item, i) => (
            <div
              key={i}
              className={`fnode fnode-out ${litNodes.out.includes(i) ? "lit" : ""}`}
            >
              <span className="fnode-icon">{item.icon}</span>
              {item.label}
            </div>
          ))}
        </div>

        {/* Server */}
        <div className="server-node">
          <div className="server-box">
            <div className="srv-ico">🏛️</div>
            <span className="srv-lbl">NPHIES</span>
            <span className="srv-sub">National Server</span>
          </div>
        </div>

        {/* Nodes Right */}
        <div className="col-r">
          {[
            { icon: "✅", label: "Verified" },
            { icon: "✔️", label: "Approved" },
            { icon: "🔓", label: "Granted" },
            { icon: "📨", label: "Processed" },
            { icon: "💰", label: "Settled" },
            { icon: "📩", label: "Delivered" },
            { icon: "📈", label: "Generated" },
          ].map((item, i) => (
            <div
              key={i}
              className={`fnode fnode-in ${litNodes.in.includes(i) ? "lit" : ""}`}
            >
              <span className="fnode-icon">{item.icon}</span>
              {item.label}
            </div>
          ))}
        </div>

        {/* Animated Packets */}
        {packets.map((p, idx) => (
          <div
            key={`${p.type}-${p.id}-${idx}`}
            className={`packet ${p.type === "out" ? "pkt-out" : "pkt-in"}`}
            style={{
              left: `${p.x}px`,
              top: `${p.y}px`,
              opacity: p.opacity,
              transform: "translate(-50%, -50%)",
              transition: "opacity 0.2s",
            }}
          >
            {p.label}
          </div>
        ))}
      </div>

      {/* Scrolling Logs */}
      <div className="log-bar">
        <div className="log-led"></div>
        <div className="log-scroll">
          <div className="log-track">
            {[1, 2].map((loop) => (
              <React.Fragment key={loop}>
                <span className="log-item">
                  <em>Eligibility #4421</em> → <b>Verified</b>
                </span>
                <span className="log-item">
                  <em>Pre-Auth #8812</em> → <b>Approved</b>
                </span>
                <span className="log-item">
                  <em>Adv.PreAuth #0031</em> → <b>Granted</b>
                </span>
                <span className="log-item">
                  <em>Claim #7743</em> → <b>Processed</b>
                </span>
                <span className="log-item">
                  <em>Payment #2290</em> → <b>Settled</b>
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   MAIN COMPONENT: LOGIN PAGE
   ============================================================ */
const LoginPage = () => {
  const { t } = useTranslation();
  const { setUser, setToken, setPermissions } = useContext(appContext);
  const [showLoader, setShowLoader] = useState(false);

  // Mutation logic from Login.js
  const [loginUser] = useMutation(usersGQL.USER_LOGIN_MUTATION, {
    onCompleted: (data) => {
      setShowLoader(false);
      handleSuccessfulLogin(data);
    },
    onError: (error) => {
      setShowLoader(false);
      toast.error(
        `Invalid Credentials - ${error.graphQLErrors[0]?.message || error.message}`,
      );
    },
  });

  const handleSuccessfulLogin = (data) => {
    if (data?.userSignIn?.data?.jwt) {
      const jwt = data.userSignIn.data.jwt;
      const permissions = parsePermissions(data.userSignIn.data.permission);
      const user = {
        FullName: data.userSignIn.data.full_name,
        Email: data.userSignIn.data.email,
        RoleId: data.userSignIn.data.role_id,
        RoleName: data.userSignIn.data.role_name,
      };

      setToken(jwt);
      setPermissions(permissions);
      setUser(user);

      localStorage.setItem("token", JSON.stringify(jwt));
      localStorage.setItem("permissions", JSON.stringify(permissions));
      localStorage.setItem("user", JSON.stringify(user));
    }
  };

  const handleSubmit = (values) => {
    setShowLoader(true);
    loginUser({
      variables: { input: { email: values.email, password: values.password } },
    });
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  return (
    <div className="login-page-container">
      <div className="login-bg">
        <div className="login-bg-grid"></div>
      </div>

      <div className="page-wrapper">
        {/* Mobile Nav */}
        <nav className="mobile-topnav">
          <div className="brand-logo">S</div>
          <div className="brand-text">
            <div className="brand-name">SuperGit</div>
            <div className="brand-tag">Enterprise Portal</div>
          </div>
        </nav>

        <main className="main-layout">
          {/* Left Panel */}
          <section className="left-panel">
            <div className="desktop-brand">
              <div className="brand-logo">S</div>
              <div className="brand-text">
                <div className="brand-name">SuperGit</div>
                <div className="brand-tag">Enterprise Portal</div>
              </div>
            </div>

            <div className="hero-content">
              <div className="eyebrow">Real-Time NPHIES Routing</div>
              {/* <h1 className="hero-h1">
                7 Transaction Types.
                <br />
                <span className="grad">One Unified Gateway.</span>
              </h1> */}
              <span className="hero-h1 grad">One Unified Gateway.</span>

              <p className="hero-sub">
                Eligibility, preauthorizations, claims, payments and more —
                routed instantly to NPHIES with full visibility, automated
                maintenance, and instant acknowledgement.
              </p>
            </div>

            <DataFlowAnimation />
          </section>

          {/* Right Panel */}
          <section className="right-panel relative">
            <img
              src="images/supergit-03.png"
              alt=""
              className="size-20 mb-3 mx-auto"
            />
            <div className="login-eye mx-auto">Provider Access</div>
            <h2 className="login-h2 mx-auto">Welcome Back</h2>
            <p className="login-sub mx-auto">Sign in to the SuperGit portal</p>

            <Formik
              initialValues={{ email: "", password: "", remember: true }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, handleChange, handleBlur }) => (
                <Form className="login-form-box">
                  <div className="form-group">
                    {/* <label className="form-label">Email / Provider ID</label> */}
                    <label className="form-label">Email</label>
                    <div className="input-wrap">
                      <svg
                        className="ico"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <Field
                        className="form-input"
                        name="email"
                        type="email"
                        placeholder="provider@hospital.sa"
                      />
                    </div>
                    {errors.email && touched.email && (
                      <div className="text-red-500 text-[10px] mt-1">
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div className="input-wrap">
                      <svg
                        className="ico"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                      <Field
                        className="form-input"
                        name="password"
                        type="password"
                        placeholder="••••••••••••"
                      />
                    </div>
                    {errors.password && touched.password && (
                      <div className="text-red-500 text-[10px] mt-1">
                        {errors.password}
                      </div>
                    )}
                  </div>

                  <button
                    className="btn-primary-styled"
                    type="submit"
                    disabled={showLoader}
                  >
                    {showLoader ? "Signing In..." : "Sign In to Portal →"}
                  </button>
                </Form>
              )}
            </Formik>

            <div className="w-full flex justify-end absolute bottom-2 right-6">
              <p className="bg-gray-100 py-1.5 px-3 text-gray-600 text-xs font-semibold rounded-full ">
                SuperGit (NPHIES) v4.0
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default LoginPage;
